import type { DiagnosticSnapshot, SectionEnvelope } from "@/api/diagnostic";

/**
 * O quadro do Digital Access no Ato 1 (#1346 fatia 9, EF-FUNC-005 RF-04 e RF-05).
 *
 * Fica fora do `diagnosticData.ts` pela regra de convivência do épico: cada EF com
 * o seu seletor, para as PRs não disputarem o mesmo arquivo.
 *
 * Junta duas leituras. A seção `digital_access_estimate` é o dado de máquina, o que
 * a Calibragem congela: total lançado, tabela, se o item existe na origem e se a
 * contagem bateu no teto. O domínio `digital_access` do bloco `validation` traz os
 * criados por usuário de integração e os documentos de Digital Access já com as
 * decisões por usuário, lidos ao vivo pelo backend. Sem o domínio, valem os
 * criados da seção, que é a regra da EF sem decisão nenhuma.
 */

/** Os nove tipos de documento da SAP, na ordem e com o número do PDF da SAP. */
export const DA_DOC_TYPES = [
  "sales",
  "purchase",
  "invoice",
  "manufacturing",
  "material",
  "quality",
  "service_maintenance",
  "financial",
  "time_management",
] as const;

export type DaDocType = (typeof DA_DOC_TYPES)[number];

/** Nome da SAP de cada tipo ("SAP Digital Access for SAP S/4HANA and SAP ERP", v1.5). */
export const DA_DOC_TYPE_NAMES: Record<DaDocType, string> = {
  sales: "Sales Document",
  purchase: "Purchase Document",
  invoice: "Invoice Document",
  manufacturing: "Manufacturing Document",
  material: "Material Document",
  quality: "Quality Management Document",
  service_maintenance: "Service & Maintenance Document",
  financial: "Financial Document",
  time_management: "Time Management Document",
};

/** Nome da SAP de cada item, o mesmo do catálogo do connector (`digital_access_catalog.py`). */
export const DA_ITEM_NAMES: Record<string, string> = {
  "4031": "Sales Order Line Item",
  "4032": "Sales Contract Line Item",
  "4033": "Sales Quote Line Item",
  "4034": "Sales Sched. Agreement Releases",
  "4041": "Purchase Order Line Item",
  "4042": "Purch. Requisition Line Item",
  "4043": "Purch. Sched. Agreement Releases",
  "4051": "Billing Document Line Item",
  "4052": "Billing Doc. Request Line Item",
  "4053": "Supplier Invoice Line Item",
  "4061": "Production Order",
  "4062": "Process Order",
  "4063": "REM Confirmation",
  "4071": "Material Document Line Item",
  "4081": "Defect",
  "4082": "Quality Notification",
  "4083": "Inspection Result",
  "4084": "Inspection Point Result",
  "4091": "Maintenance Order",
  "4092": "Maintenance Notification",
  "4093": "Maintenance Confirmation",
  "4094": "Warranty Claim",
  "4095": "Service Order",
  "4096": "Service Notification",
  "4097": "Service Confirmation",
  "4121": "S/4 Service Order",
  "4123": "S/4 Service Confirmation",
  "4101": "Financial Document Items",
  "4111": "Time Sheet Record",
  "4112": "Time Management Record",
};

/** Os itens de cada tipo, como no catálogo do connector. */
export const DA_ITEMS_BY_TYPE: Record<DaDocType, readonly string[]> = {
  sales: ["4031", "4032", "4033", "4034"],
  purchase: ["4041", "4042", "4043"],
  invoice: ["4051", "4052", "4053"],
  manufacturing: ["4061", "4062", "4063"],
  material: ["4071"],
  quality: ["4081", "4082", "4083", "4084"],
  service_maintenance: ["4091", "4092", "4093", "4094", "4095", "4096", "4097", "4121", "4123"],
  financial: ["4101"],
  time_management: ["4111", "4112"],
};

/** Item sem número que é zero de verdade: a tabela não está no dicionário do cliente ou ele não configurou o tipo. */
const ZERO_BY_NATURE = new Set(["not_applicable", "table_missing", "not_configured"]);

const ITEM_DOC_TYPE: Record<string, DaDocType> = Object.fromEntries(
  DA_DOC_TYPES.flatMap((docType) => DA_ITEMS_BY_TYPE[docType].map((id) => [id, docType])),
);

/** Documentos de um usuário somados por tipo, só os tipos com algum documento. */
export function documentsByType(byItem: Record<string, number>): Partial<Record<DaDocType, number>> {
  const out: Partial<Record<DaDocType, number>> = {};
  for (const [itemId, documents] of Object.entries(byItem)) {
    const docType = ITEM_DOC_TYPE[itemId];
    if (!docType || !documents) continue;
    out[docType] = (out[docType] ?? 0) + documents;
  }
  return out;
}

export interface DaItem {
  itemId: string;
  docType: DaDocType;
  name: string;
  tables: string[];
  /** `false` quando o item não existe na origem (4112 no S/4): não é zero. */
  applicable: boolean;
  collected: boolean;
  /** Algum número do item é piso (a estimativa ou o total lançado). */
  truncated: boolean;
  /** Os criados e os documentos de Digital Access são piso (#1521). */
  estimateFloor: boolean;
  /** O total lançado é piso: no 4101 ele corta sozinho desde a #1520. */
  totalFloor: boolean;
  /** Existe na origem e ficou sem número (orçamento, falha): os totais viram piso. */
  uncounted: boolean;
  /** Total lançado no ano, por qualquer criador. */
  total: number | undefined;
  /** Criados por usuário de integração, com as decisões por usuário aplicadas. */
  counted: number | undefined;
  factor: number | undefined;
  /** `counted` × `factor`: documentos de Digital Access, nunca bloco. */
  daDocuments: number | undefined;
}

export interface DaTypeGroup {
  docType: DaDocType;
  /** O número do tipo no PDF da SAP, de 1 a 9. */
  position: number;
  items: DaItem[];
}

export interface DaTotals {
  total: number;
  counted: number;
  daDocuments: number;
  /** Algum item somado é piso: o total também é. */
  truncated: boolean;
  /** O total lançado somado é piso. */
  totalFloor: boolean;
  /** Os criados e os documentos somados são piso. */
  estimateFloor: boolean;
}

export interface DaDecisions {
  excluded: number;
  included: number;
  awaitingClient: number;
}

export interface DigitalAccessFrame {
  basisYear: string | undefined;
  periodFrom: string | undefined;
  periodTo: string | undefined;
  /** Nenhum número cortou. */
  complete: boolean;
  /** A estimativa está inteira, mesmo que o total lançado tenha cortado (#1521). */
  estimateComplete: boolean;
  /** Tipos de usuário que contam (C, S e, desde 15/10/2026, B). */
  countedUserTypes: string[];
  /** No 4101 conta só o ledger principal com origem no FI (#1396). */
  ledgerRestricted: boolean;
  groups: DaTypeGroup[];
  totals: DaTotals;
  /** `null` sem o domínio: o quadro mostra a regra da EF, sem decisão. */
  decisions: DaDecisions | null;
}

function rec(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function list(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function count(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : undefined;
}

function text(value: unknown): string | undefined {
  return typeof value === "string" && value ? value : undefined;
}

function isDocType(value: unknown): value is DaDocType {
  return typeof value === "string" && (DA_DOC_TYPES as readonly string[]).includes(value);
}

/**
 * O envelope do quadro. Sem a seção (backend anterior à #1346), o quadro fica
 * fechado como "não coletado", e não some: a ausência é dita, não escondida.
 */
export function digitalAccessEnvelope(
  sections: DiagnosticSnapshot["sections"] | undefined,
): SectionEnvelope {
  const envelope = (sections as Record<string, SectionEnvelope | undefined> | undefined)
    ?.digital_access_estimate;
  if (envelope) return envelope;
  return {
    code: "NO_PRODUCER_RUN",
    state: "not_collected",
    reason: "",
    source: { kind: "run", ref: "extraction_summary.digital_access", snapshot: "functional" },
    data: {},
  } as SectionEnvelope;
}

/** Os criados e documentos do domínio, por item, quando ele está pronto. */
function domainItems(
  validation: DiagnosticSnapshot["validation"] | undefined,
): { byItem: Map<string, Record<string, unknown>>; decisions: DaDecisions } | null {
  const envelope = (validation as Record<string, SectionEnvelope | undefined> | undefined)
    ?.digital_access;
  if (!envelope || envelope.state !== "ok") return null;
  const data = rec(envelope.data);
  const byItem = new Map<string, Record<string, unknown>>();
  for (const raw of list(data.items)) {
    const item = rec(raw);
    const id = text(item.item_id);
    if (id) byItem.set(id, item);
  }
  const users = rec(data.users);
  return {
    byItem,
    decisions: {
      excluded: count(users.excluded) ?? 0,
      included: count(users.included) ?? 0,
      awaitingClient: count(users.awaiting_client) ?? 0,
    },
  };
}

/** Os números do quadro, ou `null` quando a seção não está pronta. */
export function digitalAccessFrame(
  sections: DiagnosticSnapshot["sections"] | undefined,
  validation: DiagnosticSnapshot["validation"] | undefined,
): DigitalAccessFrame | null {
  const envelope = digitalAccessEnvelope(sections);
  if (envelope.state !== "ok" && envelope.state !== "partial" && envelope.state !== "truncated") {
    return null;
  }
  const data = rec(envelope.data);
  const domain = domainItems(validation);
  const byType = new Map<DaDocType, DaItem[]>();
  let ledgerRestricted = false;

  for (const raw of list(data.da_items)) {
    const item = rec(raw);
    const itemId = text(item.item_id);
    // NF-e fica fora: não é item do Digital Access (EF-FUNC-005 RF-04).
    if (!itemId || !isDocType(item.doc_type)) continue;
    const applicable = item.applicable !== false;
    const collected = applicable && item.collected !== false;
    const factor = count(item.factor);
    const fromDomain = domain?.byItem.get(itemId);
    const counted = collected
      ? count(fromDomain ? fromDomain.tech_user_total : item.tech_user_total)
      : undefined;
    const domainDocuments = fromDomain ? count(fromDomain.da_documents) : undefined;
    const daDocuments =
      domainDocuments ??
      (counted !== undefined && factor !== undefined ? counted * factor : undefined);
    if (itemId === "4101" && "leading_ledger_fi_origin" in rec(item.variants)) {
      ledgerRestricted = true;
    }
    // Execução anterior à #1520 não separa os pisos: vale o `truncated` em tudo.
    const truncated = item.truncated === true;
    const row: DaItem = {
      itemId,
      docType: item.doc_type,
      name: DA_ITEM_NAMES[itemId] ?? itemId,
      tables: list(item.tables).filter((t): t is string => typeof t === "string"),
      applicable,
      collected,
      truncated,
      estimateFloor: typeof item.estimate_truncated === "boolean" ? item.estimate_truncated : truncated,
      totalFloor: typeof item.total_truncated === "boolean" ? item.total_truncated : truncated,
      uncounted: applicable && !collected && !ZERO_BY_NATURE.has(text(item.reason) ?? ""),
      total: collected ? count(item.total) : undefined,
      counted,
      factor,
      daDocuments: collected ? daDocuments : undefined,
    };
    byType.set(item.doc_type, [...(byType.get(item.doc_type) ?? []), row]);
  }
  if (byType.size === 0) return null;

  const groups = DA_DOC_TYPES.map((docType, index) => ({
    docType,
    position: index + 1,
    items: byType.get(docType) ?? [],
  }));
  const all = groups.flatMap((group) => group.items);
  const totals: DaTotals = {
    total: all.reduce((sum, item) => sum + (item.total ?? 0), 0),
    counted: all.reduce((sum, item) => sum + (item.counted ?? 0), 0),
    daDocuments: all.reduce((sum, item) => sum + (item.daDocuments ?? 0), 0),
    truncated: all.some((item) => item.truncated && item.total !== undefined),
    // Item sem número também faz da soma um piso, como o nó 06 diz pela estimativa.
    totalFloor: all.some((item) => (item.totalFloor && item.total !== undefined) || item.uncounted),
    estimateFloor: all.some((item) => (item.estimateFloor && item.counted !== undefined) || item.uncounted),
  };
  const userTypes = rec(data.user_types);
  const countedUserTypes = list(userTypes.counted).filter(
    (t): t is string => typeof t === "string",
  );
  return {
    basisYear: text(data.basis_year),
    periodFrom: text(data.period_from),
    periodTo: text(data.period_to),
    complete: data.complete === true,
    estimateComplete:
      typeof data.estimate_complete === "boolean" ? data.estimate_complete : data.complete === true,
    // Quem conta vem declarado no envelope (a regra mora no agents, #1792): sem padrão aqui.
    countedUserTypes,
    ledgerRestricted,
    groups,
    totals,
    decisions: domain ? domain.decisions : null,
  };
}

/**
 * Item medido e sem volume nenhum fica recolhido até "Mostrar todos". O que não
 * se aplica à origem, ou não foi medido, continua à mostra: é afirmação, não zero.
 */
export function hiddenByDefault(item: DaItem): boolean {
  return item.collected && (item.total ?? 0) === 0 && (item.counted ?? 0) === 0;
}

/**
 * Fração dos lançados que veio de integração, em % inteiro; `null` quando não se
 * lê. Abaixo de 1% sai "<1%": "0%" ao lado de um número positivo se lê como
 * contradição.
 */
export function countedShare(item: Pick<DaItem, "total" | "counted">): string | null {
  if (!item.total || !item.counted) return null;
  const share = Math.round((item.counted / item.total) * 100);
  return share < 1 ? "<1%" : `${share}%`;
}

/** Um tipo de documento no Volume real do Ato 1 (#1713, cartão 5.2): o total lançado do quadro. */
export interface DaTypeVolume {
  docType: DaDocType;
  /**
   * Soma do total lançado dos itens medidos. `undefined` sem item medido, e também
   * quando o medido soma zero e algum item ficou sem número: "pelo menos 0" não diz
   * nada, e o tipo é tão desconhecido quanto o que não foi medido.
   */
  total: number | undefined;
  /** Algum item cortou no teto: o total é piso, como o `+` do quadro. */
  cut: boolean;
  /** Algum item existe na origem e ficou sem número: o total também é piso. */
  uncounted: boolean;
  /** Nenhum item do tipo existe na origem. */
  notApplicable: boolean;
}

/**
 * O total lançado de cada um dos nove tipos, somado como o quadro soma: o Marco
 * decidiu em 22/10/2026 que o Volume real usa a contagem do Digital Access, para os
 * números do card e do quadro baterem.
 */
export function volumesByType(frame: DigitalAccessFrame): DaTypeVolume[] {
  return frame.groups.map((group) => {
    const measured = group.items.filter((item) => item.total !== undefined);
    const sum = measured.reduce((total, item) => total + (item.total ?? 0), 0);
    const uncounted = group.items.some((item) => item.uncounted);
    return {
      docType: group.docType,
      total: measured.length > 0 && !(sum === 0 && uncounted) ? sum : undefined,
      cut: measured.some((item) => item.totalFloor),
      uncounted,
      notApplicable: group.items.length > 0 && group.items.every((item) => !item.applicable),
    };
  });
}

/**
 * Fatura de venda e documento de material: os itens que um job cria a partir de
 * outro documento (o faturamento em lote a partir da remessa, por exemplo), e que o
 * contrato não cobra (#1713, cartão 5.1).
 */
export const SUBSEQUENT_DOCUMENT_ITEMS = ["4051", "4071"] as const;

/**
 * Os itens de documento subsequente com documento de usuário técnico, já com as
 * decisões por usuário. O Marco decidiu em 22/10/2026 que a decisão por usuário
 * basta: a tela avisa, e o consultor exclui o usuário do job.
 */
export function subsequentDocumentItems(frame: DigitalAccessFrame): string[] {
  const counted = new Map(
    frame.groups.flatMap((group) => group.items).map((item) => [item.itemId, item.counted ?? 0]),
  );
  return SUBSEQUENT_DOCUMENT_ITEMS.filter((itemId) => (counted.get(itemId) ?? 0) > 0);
}
